import React, { useState, useRef, useEffect } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { _get, _getBaseUrl } from "./axios";
import { useToast } from "@/hooks/useToast";
import { ChevronDown } from "lucide-react";

const DownloadModal = ({
  show,
  onHide,
  id
}: {
  show: boolean;
  onHide: () => void;
  id: string
}) => {
  const [format, setFormat] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { toast } = useToast();

  const handleSelectChange = (selectedFormat: string) => {
    setFormat(selectedFormat);
    setIsDropdownOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const downloadFile = async () => {
    setDownloading(true);

    const url = `/categorizer/download/${id}/${format}`;

    try {
        const response = await _get<{ presigned_url: string }>(url);
        const data = response.data;

        if (response.status !== 200) {
            toast({
                type: "error",
                title: "Download failed",
                message: "Unable to generate download link. Please try again.",
            });
            return;
        }

        const redirectUrl = data.presigned_url;
        
        if (redirectUrl) {
            window.location.href = redirectUrl;
            
            setTimeout(() => {
                onHide();
            }, 2000);
        } else {
            toast({
                type: "error",
                title: "Download failed",
                message: "No download link received from server. Please try again.",
            });
        }
    } catch (error) {
        toast({
            type: "error",
            title: "Download failed",
            message: "Something went wrong, please check your internet connection and refresh the page, thank you.",
        });
    } finally {
        setTimeout(() => {
            setDownloading(false);
        }, 3000);
    }
};

  return (
    <Modal
      show={show}
      onHide={onHide}
      aria-labelledby="contained-modal-title-vcenter"
      dialogClassName="download-modal-dialog"
      centered
    >
      <Modal.Header>
        <Modal.Title id="modal-title">Download Model</Modal.Title>
      </Modal.Header>
      <Modal.Body className="body">
        <div className="model-selector">
          <Form>
            <Form.Group
              className="form-group-custom"
              controlId="exampleForm.SelectCustom"
            >
              <Form.Label className="form-label-custom">
                {" "}
                Download Format
              </Form.Label>
              <div 
                ref={dropdownRef}
                style={{ position: 'relative', width: '100%' }}
              >
                <div
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  style={{
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    border: '1px solid #ced4da',
                    padding: '8px 12px',
                    fontSize: '14px',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    minHeight: '38px'
                  }}
                >
                  <span style={{ color: format ? '#000' : '#6c757d' }}>
                    {format || 'select format'}
                  </span>
                  <span><ChevronDown size={16} /></span>
                </div>
                
                {isDropdownOpen && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      backgroundColor: 'white',
                      borderRadius: '8px',
                      border: '1px solid #ced4da',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      zIndex: 1000,
                      marginTop: '2px'
                    }}
                  >
                    <div
                      onClick={() => handleSelectChange('excel')}
                      style={{
                        padding: '8px 12px',
                        cursor: 'pointer',
                        borderRadius: '8px 8px 0 0',
                        transition: 'background-color 0.15s ease-in-out'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#f8f9fa';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'white';
                      }}
                    >
                      Excel
                    </div>
                    <div
                      onClick={() => handleSelectChange('csv')}
                      style={{
                        padding: '8px 12px',
                        cursor: 'pointer',
                        borderRadius: '0 0 8px 8px',
                        transition: 'background-color 0.15s ease-in-out'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#f8f9fa';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'white';
                      }}
                    >
                      CSV
                    </div>
                  </div>
                )}
              </div>
            </Form.Group>
          </Form>
        </div>
      </Modal.Body>
      <Modal.Footer id="modal-footer">
        <Button className="cancel-btn" onClick={onHide}>
          Cancel
        </Button>
        <Button className="submit-btn" disabled={!format || downloading} onClick={() => downloadFile()}>{downloading ? "Downloading...": "Submit"}</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DownloadModal;
